import { X } from "lucide-react"
import { Controller, type Control, type UseFormSetValue } from "react-hook-form"

import { IndustrySelector } from "@/components/industry-selector"
import { LabeledMultiSelect, type LabeledOption } from "@/components/label-multiselect"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { FilterFormData } from "@/lib/search-filter-model"
import { CONTACT_OUT_COMPANY_SIZES } from "@/types/contactOut"

interface CompanyFiltersSectionProps {
  control: Control<FilterFormData>
  formValues: FilterFormData
  onAddCompany: () => void
  onAddExcludedCompany: () => void
  onRemoveCompany: (company: string) => void
  onRemoveExcludedCompany: (company: string) => void
  setValue: UseFormSetValue<FilterFormData>
}

export function CompanyFiltersSection({
  control,
  formValues,
  onAddCompany,
  onAddExcludedCompany,
  onRemoveCompany,
  onRemoveExcludedCompany,
  setValue,
}: CompanyFiltersSectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label>Companies</Label>
        </div>
        <Controller
          name="companyInput"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Google, Microsoft, Apple (separate with commas)"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  onAddCompany()
                }
              }}
            />
          )}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Press Enter to add companies, or separate multiple companies with commas
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {formValues.companies.map((company) => (
            <Badge key={company} variant="secondary" className="flex items-center gap-1">
              <span className="rounded bg-blue-100 px-1 text-xs text-blue-800">COMPANY</span>
              {company}
              <X className="h-3 w-3 cursor-pointer" onClick={() => onRemoveCompany(company)} />
            </Badge>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label>Excluded Companies</Label>
        </div>
        <Controller
          name="excludedCompanyInput"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Google, Microsoft, Apple (separate with commas)"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  onAddExcludedCompany()
                }
              }}
            />
          )}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Press Enter to add excluded companies, or separate multiple companies with commas
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {formValues.excludedCompanies.map((company) => (
            <Badge key={company} variant="destructive" className="flex items-center gap-1">
              <span className="rounded bg-red-100 px-1 text-xs text-red-800">EXCLUDED</span>
              {company}
              <X className="h-3 w-3 cursor-pointer" onClick={() => onRemoveExcludedCompany(company)} />
            </Badge>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <IndustrySelector
          selectedIndustries={formValues.companyIndustries || []}
          onIndustriesChange={(industries) => setValue("companyIndustries", industries, { shouldDirty: true })}
          placeholder="Search industries..."
          label="Company Industries"
        />
        <LabeledMultiSelect
          onSelectedValuesChange={(values) => setValue("companySizes", values, { shouldDirty: true })}
          selectedValues={formValues.companySizes}
          placeholder="Select company sizes"
          options={CONTACT_OUT_COMPANY_SIZES as unknown as LabeledOption[]}
          label="Desired company sizes"
          bagdeTitle="size"
        />
      </div>
    </div>
  )
}
